import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { GameSettingsService } from "../../../game-settings.service";
import { environment } from "@env/environment";
import { Reward } from "../../reward-selector.component";

@Component({
  selector: "app-reward-editor",
  templateUrl: "./reward-editor.component.html",
  styleUrls: ["./reward-editor.component.scss"],
})
export class RewardEditorComponent implements OnInit, OnChanges {
  @Output() formReady = new EventEmitter<FormGroup>(); // emit form to parent
  @Input() startPositive: boolean = true;
  @Input() reward: Reward | undefined = undefined;
  contentForm: FormGroup;
  s3BucketUrl = environment.s3BucketUrl;

  constructor(
    private fb: FormBuilder,
    public gameSettingsService: GameSettingsService
  ) {}

  ngOnInit() {
    const reward: any = this.reward || {};

    const { type, coinIcon, points, counter, thumbnail, background } = reward;

    const defaultCoinIcon = coinIcon
      ? coinIcon
      : this.gameSettingsService.defaultCoinIcon.value;

    const thumbnailImage =
      thumbnail && thumbnail.image ? thumbnail.image : undefined;

    const thumbnailScale = thumbnail && thumbnail.scale ? thumbnail.scale : 1.0;

    const backgroundImage =
      background && background.image ? background.image : undefined;

    const backgroundScale =
      background && background.scale ? background.scale : 1.0;

    this.contentForm = this.fb.group({
      rewardType: [type],
      coinIcon: [defaultCoinIcon, Validators.required],
      thumbnail: [thumbnailImage, Validators.required],
      thumbnailScale: [thumbnailScale],
      background: [backgroundImage, Validators.required],
      backgroundScale: [backgroundScale],
      backgroundColor1: [
        background && background.color1 ? background.color1 : "#e6e6e6",
      ],
      backgroundColor2: [
        background && background.color2 ? background.color2 : "#e6e6e6",
      ],
      counterColor1: [counter && counter.color1 ? counter.color1 : "#ff5acd"],
      counterColor2: [counter && counter.color2 ? counter.color2 : "#fbda61"],
      points: [points || "", Validators.required],
    });

    // emit form to parent for data access
    this.formReady.emit(this.contentForm);

    if (!this.contentForm.value.coinIcon) {
      this.gameSettingsService
        .getDefaultCoinIcons()
        .then((icons: any) => {
          if (icons && icons.points && icons.points.value) {
            this.contentForm.patchValue({ coinIcon: icons.points.value });
          }
        })
        .catch(() => {
          this.contentForm.patchValue({
            coinIcon: "/assets/images/image-not-found-150x150.png",
          });
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.startPositive && this.contentForm) {
      this.contentForm.patchValue({ rewardType: this.startPositive });
    }
  }

  resolveSrc(value: string): string {
    if (!value) {
      return "/assets/images/image-not-found-150x150.png";
    }
    const v = String(value).trim();
    if (/^(data:|https?:|blob:)/i.test(v)) {
      return v;
    }
    if (v.startsWith("/assets/")) {
      return v;
    }
    return this.s3BucketUrl + v;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  onCompactApply(evt: { blob: Blob; filters: any }, controlName: 'coinIcon' | 'thumbnail' | 'background') {
    if (!evt || !evt.blob) {
      return;
    }
    this.blobToBase64(evt.blob).then((base64) => {
      this.contentForm.patchValue({ [controlName]: base64 });
    }).catch(() => {
      // ignore read errors
    });
  }

  uploadImage(event: any, controlName: string) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.contentForm.patchValue({ [controlName]: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  }
}
